import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Star, Clock } from "lucide-react";
import { Badge } from "./ui/badge";
import { projects, PROJECT_TAGS, TAG_COLORS } from "../data/mock";

const ICONS = { Star, Clock };

const getCardThumbnail = (image) => {
  const item = Array.isArray(image) ? image[0] : image;
  if (item && typeof item === "object") return item.src || item.poster || null;
  return item || null;
};

const Projects = () => {
  const categories = useMemo(() => {
    const set = new Set(projects.map((p) => p.category));
    return ["All", ...Array.from(set)];
  }, []);
  const [active, setActive] = useState("All");

  const filtered = active === "All" ? projects : projects.filter((p) => p.category === active);

  return (
    <section id="projects" className="relative py-24 lg:py-32 border-t border-teal-500/10">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12">
          <div>
            <div className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.2em] text-teal-400 mb-4">
              <span className="w-8 h-px bg-teal-400" /> 05 — Personal Projects
            </div>
            <h2 className="font-display text-4xl lg:text-5xl font-bold leading-tight">
              Things I&apos;ve <span className="text-teal-300">designed, built</span>
              <br />and broken (then fixed).
            </h2>
          </div>
          <p className="text-slate-400 max-w-md">
            A curated set of academic, club, and personal projects that span CAD, simulation, robotics, and manufacturing.
          </p>
        </div>

        <div className="flex flex-wrap gap-2 mb-10">
          {categories.map((c) => (
            <button
              key={c}
              onClick={() => setActive(c)}
              className={`px-4 py-2 rounded-full text-sm border transition-all ${
                active === c
                  ? "bg-teal-400 text-[#04100f] border-teal-400 font-medium"
                  : "bg-transparent text-slate-300 border-teal-500/20 hover:border-teal-400/60 hover:text-teal-200"
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map((p) => (
            <article
              key={p.id}
              className="group relative rounded-2xl overflow-hidden border border-teal-500/15 bg-[#08201d]/60 hover:border-teal-400/50 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_20px_60px_-20px_rgba(20,184,166,0.4)]"
            >
              <Link
                to={`/projects/${p.slug}`}
                className="absolute inset-0 z-10"
                aria-label={`View ${p.title} case study`}
              />
              <div className="relative h-52 overflow-hidden">
                <img
                  src={getCardThumbnail(p.image)}
                  alt={p.title}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#04100f] via-[#04100f]/40 to-transparent" />
                {(p.labels || []).length > 0 && (
                  <div className="absolute top-3 left-3 flex flex-wrap gap-1.5">
                    {p.labels.map((key) => {
                      const tag = PROJECT_TAGS[key];
                      if (!tag) return null;
                      const color = TAG_COLORS[tag.colorId];
                      const Icon = tag.icon ? ICONS[tag.icon] : null;
                      return (
                        <div key={key} className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold ${color.badge}`}>
                          {Icon && <Icon className="w-3 h-3" />}
                          {tag.label}
                        </div>
                      );
                    })}
                  </div>
                )}
                <div className="absolute top-3 right-3 px-2 py-1 rounded-md bg-black/40 backdrop-blur text-xs font-mono text-teal-200 border border-teal-500/30">
                  {p.year}
                </div>
              </div>

              <div className="p-6">
                <div className="text-xs font-mono uppercase tracking-widest text-teal-400 mb-2">
                  {p.category}
                </div>
                <h3 className="font-display text-xl font-semibold text-slate-100 group-hover:text-teal-200 transition-colors">
                  {p.title}
                </h3>
                <p className="mt-2 text-sm text-slate-400 leading-relaxed">{p.summary}</p>

                {p.metrics && (
                  <div className="mt-4 grid grid-cols-2 gap-2">
                    {p.metrics.map((m) => (
                      <div
                        key={m.label}
                        className="rounded-lg bg-teal-500/5 border border-teal-500/15 px-3 py-2"
                      >
                        <div className="text-base font-semibold text-teal-300">
                          {m.value}
                        </div>
                        <div className="text-[10px] uppercase tracking-wider text-slate-400">
                          {m.label}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                <div className="mt-5 flex flex-wrap gap-1.5">
                  {p.tags.map((t) => (
                    <Badge
                      key={t}
                      variant="outline"
                      className="border-teal-500/25 bg-transparent text-slate-300 text-[11px] font-normal"
                    >
                      {t}
                    </Badge>
                  ))}
                </div>

                <button
                  onClick={() => {}}
                  className="relative z-20 mt-5 inline-flex items-center gap-1.5 text-sm text-teal-300 hover:text-teal-200 transition-colors pointer-events-none"
                >
                  Read case study
                  <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </button>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Projects;
